import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useMeetingsQuery } from "@/hooks/useMeetingsQuery";
import { useEnrichedMeetings } from "@/hooks/useMeetingIntelligence";
import { useMeetingDetail } from "@/hooks/useMeetingDetail";
import type { MeetingIntelligence } from "@/lib/api";
import { Route } from "@/routes/_authenticated/dashboard/meetings";
import {
  AlertTriangle,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  History,
  MessageSquare,
  Users,
} from "lucide-react";

import { type MeetingsTabType } from "@/components/meetings/tabs";

// ---------------------------------------------------------------------------
// Animated collapsible wrapper (CSS max-height transition, no extra deps)
// ---------------------------------------------------------------------------

function Collapsible({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [maxH, setMaxH] = useState<string>(open ? "none" : "0px");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) {
      // Measure then animate open
      const h = el.scrollHeight;
      setMaxH(`${h}px`);
      const timer = setTimeout(() => setMaxH("none"), 280);
      return () => clearTimeout(timer);
    } else {
      // Snap to measured height first so transition has a start value
      const h = el.scrollHeight;
      setMaxH(`${h}px`);
      // Force reflow then collapse
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setMaxH("0px"));
      });
    }
  }, [open]);

  return (
    <div
      ref={ref}
      style={{
        maxHeight: maxH,
        overflow: "hidden",
        transition: "max-height 250ms cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Past meeting detail card (expandable row inside the history timeline)
// ---------------------------------------------------------------------------

function PastMeetingRow({
  meeting,
  attendeeEmails,
  formatDateTime,
}: {
  meeting: MeetingIntelligence & { _overlapCount?: number };
  attendeeEmails: Set<string>;
  formatDateTime: (d: string | null | undefined) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const { data: detail, isFetching: loadingTranscript } = useMeetingDetail(
    showTranscript ? (meeting.graph_meeting_id ?? null) : null,
  );

  const isSummarized = meeting.enrichment_status === "summarized";
  const actionCount = meeting.action_items?.length ?? 0;
  const decisionCount = meeting.decisions?.length ?? 0;
  const followUpCount = meeting.follow_ups?.length ?? 0;
  const topicCount = meeting.key_topics?.length ?? 0;

  // Shared attendees for this past meeting
  const sharedNames = useMemo(() => {
    if (!meeting.attendees) return [];
    return meeting.attendees
      .filter(
        (a) => a.email && attendeeEmails.has(a.email.toLowerCase()),
      )
      .map((a) => a.name || a.email || "")
      .slice(0, 4);
  }, [meeting.attendees, attendeeEmails]);

  return (
    <div
      style={{
        borderLeft: isSummarized
          ? "3px solid var(--accent)"
          : "3px solid var(--border)",
        marginLeft: 12,
        paddingLeft: 16,
      }}
    >
      {/* Row header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left py-2.5 flex items-start gap-2 group"
        style={{ color: "var(--text-primary)" }}
      >
        <span className="mt-0.5 flex-shrink-0 transition-transform" style={{
          transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          color: "var(--text-secondary)",
        }}>
          <ChevronRight className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {meeting.subject || "Meeting"}
            </span>
            {isSummarized && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)",
                  color: "var(--accent)",
                }}
              >
                INTEL
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            {meeting.start_time && (
              <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                {formatDateTime(meeting.start_time)}
              </span>
            )}
            {sharedNames.length > 0 && (
              <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                <Users className="w-3 h-3 inline mr-1" style={{ verticalAlign: "-1px" }} />
                {sharedNames.join(", ")}
              </span>
            )}
          </div>
          {/* Compact summary preview when collapsed */}
          {!expanded && meeting.summary && (
            <p
              className="text-xs mt-1 line-clamp-1"
              style={{ color: "var(--text-secondary)" }}
            >
              {meeting.summary}
            </p>
          )}
        </div>
        {/* Stat chips (collapsed view) */}
        {!expanded && (actionCount > 0 || decisionCount > 0) && (
          <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
            {actionCount > 0 && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{ backgroundColor: "var(--secondary)", color: "var(--text-secondary)" }}
              >
                {actionCount} action{actionCount !== 1 ? "s" : ""}
              </span>
            )}
            {decisionCount > 0 && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{ backgroundColor: "var(--secondary)", color: "var(--text-secondary)" }}
              >
                {decisionCount} decision{decisionCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Expanded detail */}
      <Collapsible open={expanded}>
        <div className="pb-3 space-y-3" style={{ paddingLeft: 20 }}>
          {/* Summary */}
          {meeting.summary && (
            <div
              className="rounded p-3"
              style={{
                backgroundColor: "var(--surface)",
                border: "1px solid var(--border)",
              }}
            >
              <h5
                className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                Summary
              </h5>
              <p className="text-xs leading-relaxed" style={{ color: "var(--text-primary)" }}>
                {meeting.summary}
              </p>
            </div>
          )}

          {/* Key Topics */}
          {topicCount > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {meeting.key_topics!.map((topic, idx) => (
                <span
                  key={idx}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--accent) 10%, transparent)",
                    color: "var(--accent)",
                  }}
                >
                  {topic}
                </span>
              ))}
            </div>
          )}

          {/* Action Items */}
          {actionCount > 0 && (
            <div>
              <h5
                className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                Action Items ({actionCount})
              </h5>
              <div className="space-y-1">
                {meeting.action_items!.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 text-xs"
                    style={{ color: "var(--text-primary)" }}
                  >
                    <CheckSquare
                      className="w-3 h-3 mt-0.5 flex-shrink-0"
                      style={{ color: "var(--success)" }}
                    />
                    <span className="flex-1">{item.description}</span>
                    {item.assignee && (
                      <span
                        className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: "var(--secondary)", color: "var(--text-secondary)" }}
                      >
                        {item.assignee}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Decisions */}
          {decisionCount > 0 && (
            <div>
              <h5
                className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                Decisions ({decisionCount})
              </h5>
              <div className="space-y-1.5">
                {meeting.decisions!.map((d, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <MessageSquare
                      className="w-3 h-3 mt-0.5 flex-shrink-0"
                      style={{ color: "var(--warning)" }}
                    />
                    <div>
                      <span style={{ color: "var(--text-primary)" }}>{d.description}</span>
                      {d.context && (
                        <p className="text-[10px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                          {d.context}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-ups */}
          {followUpCount > 0 && (
            <div>
              <h5
                className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                Follow-ups ({followUpCount})
              </h5>
              <div className="space-y-1">
                {meeting.follow_ups!.map((f, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-xs"
                    style={{ color: "var(--text-primary)" }}
                  >
                    <span style={{ color: "var(--text-secondary)" }}>--</span>
                    <span>{f.description}</span>
                    {f.owner && (
                      <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                        ({f.owner})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Referenced Files */}
          {meeting.referenced_files && meeting.referenced_files.length > 0 && (
            <div>
              <h5
                className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                Referenced Files
              </h5>
              <div className="space-y-1">
                {meeting.referenced_files.map((file: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-primary)" }}>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                    <span className="truncate">{file}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flagged Claims */}
          {meeting.flagged_claims && meeting.flagged_claims.length > 0 && (
            <div>
              <h5
                className="text-[10px] font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--text-secondary)" }}
              >
                Flagged Claims
              </h5>
              <div className="space-y-1.5">
                {meeting.flagged_claims!.map((fc, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <AlertTriangle
                      className="w-3 h-3 mt-0.5 flex-shrink-0"
                      style={{
                        color:
                          fc.accurate === false
                            ? "var(--error)"
                            : fc.accurate === true
                              ? "var(--success)"
                              : "var(--warning)",
                      }}
                    />
                    <div>
                      <span style={{ color: "var(--text-primary)" }}>{fc.claim}</span>
                      {fc.verification && (
                        <p className="text-[10px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                          {fc.verification}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transcript toggle */}
          {isSummarized && (
            <div>
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-colors"
                style={{ color: "var(--accent)" }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                <FileText className="w-3.5 h-3.5" />
                {showTranscript ? "Hide Transcript" : "View Transcript"}
              </button>
              <Collapsible open={showTranscript}>
                <div className="mt-2">
                  {loadingTranscript ? (
                    <div
                      className="text-xs py-4 text-center rounded"
                      style={{
                        backgroundColor: "var(--surface)",
                        color: "var(--text-secondary)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      Loading transcript...
                    </div>
                  ) : detail?.transcript_text ? (
                    <pre
                      className="text-xs whitespace-pre-wrap rounded p-3 max-h-80 overflow-y-auto font-mono leading-relaxed"
                      style={{
                        backgroundColor: "var(--surface)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      {detail.transcript_text}
                    </pre>
                  ) : (
                    <p className="text-xs py-2" style={{ color: "var(--text-secondary)" }}>
                      Transcript not available.
                    </p>
                  )}
                </div>
              </Collapsible>
            </div>
          )}
        </div>
      </Collapsible>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible meeting history section (shared by Graph + DB upcoming cards)
// ---------------------------------------------------------------------------

function MeetingHistorySection({
  meetingId,
  relatedPast,
  attendeeEmails,
  historyOpen,
  onToggle,
  formatDateTime,
}: {
  meetingId: string;
  relatedPast: (MeetingIntelligence & { _overlapCount?: number })[];
  attendeeEmails: Set<string>;
  historyOpen: boolean;
  onToggle: () => void;
  formatDateTime: (d: string | null | undefined) => string;
}) {
  if (relatedPast.length === 0) return null;

  const enrichedCount = relatedPast.filter(
    (m) => m.enrichment_status === "summarized",
  ).length;

  return (
    <>
      <button
        onClick={onToggle}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-left transition-colors"
        style={{
          borderTop: "1px solid var(--border)",
          backgroundColor: historyOpen ? "var(--surface)" : "transparent",
          color: "var(--text-secondary)",
        }}
        onMouseEnter={(e) => {
          if (!historyOpen)
            e.currentTarget.style.backgroundColor = "var(--surface)";
        }}
        onMouseLeave={(e) => {
          if (!historyOpen)
            e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <span
          className="transition-transform"
          style={{
            transform: historyOpen ? "rotate(90deg)" : "rotate(0deg)",
            display: "inline-flex",
          }}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </span>
        <History className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">
          {relatedPast.length} previous meeting
          {relatedPast.length !== 1 ? "s" : ""} with these attendees
        </span>
        {enrichedCount > 0 && (
          <span
            className="text-[10px] font-medium px-1.5 py-0.5 rounded ml-1"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--accent) 10%, transparent)",
              color: "var(--accent)",
            }}
          >
            {enrichedCount} with intel
          </span>
        )}
      </button>

      <Collapsible open={historyOpen}>
        <div
          className="px-4 pt-1 pb-4"
          style={{ backgroundColor: "var(--surface)" }}
        >
          {relatedPast.map((pm) => (
            <PastMeetingRow
              key={pm.id}
              meeting={pm}
              attendeeEmails={attendeeEmails}
              formatDateTime={formatDateTime}
            />
          ))}
        </div>
      </Collapsible>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MeetingsViewEnhanced() {
  const { backendToken } = useAuth();
  const { tab: initialTab } = Route.useSearch();
  const [activeTab, setActiveTab] = useState<MeetingsTabType>(initialTab ?? "recent");
  const [expandedMeetings, setExpandedMeetings] = useState<Set<string>>(
    new Set(),
  );
  const [dateRange, setDateRange] = useState<"7days" | "30days" | "90days">(
    "30days",
  );
  const [transcriptMeetingId, setTranscriptMeetingId] = useState<
    string | null
  >(null);
  // Track which upcoming meetings have their history section open
  const [openHistories, setOpenHistories] = useState<Set<string>>(new Set());

  const tokenForQuery = backendToken || undefined;

  // Graph proxy for upcoming meetings (live calendar data)
  const { data: upcomingMeetings = [], isFetching: loadingUpcoming } =
    useMeetingsQuery("upcoming", tokenForQuery, { daysAhead: 30 });

  // DB enriched meetings for Recent/Notes/Action Items
  const { data: enrichedMeetings = [], isFetching: loadingEnriched } =
    useEnrichedMeetings(100);

  // Build a lookup of enriched meeting graph IDs for the "intelligence available" indicator.
  // Only include meetings that actually have enrichment data (not just synced from calendar).
  const enrichedGraphIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of enrichedMeetings) {
      if (m.graph_meeting_id && m.enrichment_status && m.enrichment_status !== "none") {
        ids.add(m.graph_meeting_id);
      }
    }
    return ids;
  }, [enrichedMeetings]);

  // Lazy-load full meeting detail (with transcript) when requested
  const { data: meetingDetail, isFetching: loadingTranscript } =
    useMeetingDetail(transcriptMeetingId);

  // Lookup: graph_meeting_id -> enriched meeting
  const enrichedByGraphId = useMemo(() => {
    const map = new Map<string, (typeof enrichedMeetings)[number]>();
    for (const m of enrichedMeetings) {
      if (m.graph_meeting_id) map.set(m.graph_meeting_id, m);
    }
    return map;
  }, [enrichedMeetings]);

  // Past enriched meetings (for attendee-based matching on upcoming tab)
  const pastEnrichedMeetings = useMemo(() => {
    const now = new Date();
    return enrichedMeetings.filter(
      (m) => m.start_time && new Date(m.start_time) < now,
    );
  }, [enrichedMeetings]);

  /**
   * Given a set of attendee emails, find past meetings that share at least
   * one attendee. Returns ALL matches sorted by overlap count (desc), then date (desc).
   */
  const findPastMeetingsByAttendees = useCallback(
    (attendeeEmails: Set<string>, excludeMeetingId?: string) => {
      if (attendeeEmails.size === 0) return [];
      const scored = pastEnrichedMeetings
        .filter((m) => m.id !== excludeMeetingId && m.attendees?.length)
        .map((m) => {
          const emails = (m.attendees || [])
            .map((a) => a.email?.toLowerCase())
            .filter(Boolean) as string[];
          const overlap = emails.filter((e) => attendeeEmails.has(e)).length;
          return { meeting: m, overlap };
        })
        .filter((s) => s.overlap === attendeeEmails.size)
        .sort((a, b) => {
          if (b.overlap !== a.overlap) return b.overlap - a.overlap;
          const ta = a.meeting.start_time
            ? new Date(a.meeting.start_time).getTime()
            : 0;
          const tb = b.meeting.start_time
            ? new Date(b.meeting.start_time).getTime()
            : 0;
          return tb - ta;
        });
      return scored.map((s) => ({
        ...s.meeting,
        _overlapCount: s.overlap,
      }));
    },
    [pastEnrichedMeetings],
  );

  // Pre-compute related past meetings for each upcoming meeting
  const upcomingWithRelated = useMemo(() => {
    return upcomingMeetings.map((meeting) => {
      const attendeeEmails = new Set(
        (meeting.attendees || [])
          .map((a) => a.email?.toLowerCase())
          .filter(Boolean) as string[],
      );
      return {
        meeting,
        attendeeEmails,
        relatedPast: findPastMeetingsByAttendees(attendeeEmails, meeting.id),
      };
    });
  }, [upcomingMeetings, findPastMeetingsByAttendees]);

  // Filter enriched meetings by date range for the Recent tab (past only)
  const recentMeetings = useMemo(() => {
    const days = dateRange === "7days" ? 7 : dateRange === "90days" ? 90 : 30;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const now = new Date();
    return enrichedMeetings.filter((m) => {
      if (!m.start_time) return false;
      const t = new Date(m.start_time);
      return t >= cutoff && t < now;
    });
  }, [enrichedMeetings, dateRange]);

  // Future meetings from DB (for Upcoming tab, merged with Graph data)
  const upcomingFromDb = useMemo(() => {
    const now = new Date();
    return enrichedMeetings
      .filter((m) => m.start_time && new Date(m.start_time) > now)
      .sort(
        (a, b) =>
          new Date(a.start_time!).getTime() - new Date(b.start_time!).getTime(),
      );
  }, [enrichedMeetings]);

  // Meetings that have summaries (for Notes tab)
  const meetingsWithNotes = useMemo(
    () => enrichedMeetings.filter((m) => m.summary),
    [enrichedMeetings],
  );

  // All action items aggregated from enriched meetings
  const allActionItems = useMemo(() => {
    const items: Array<{
      meetingId: string;
      meetingSubject: string;
      meetingDate: string | null | undefined;
      description: string;
      assignee: string | null | undefined;
      dueDate: string | null | undefined;
    }> = [];
    for (const m of enrichedMeetings) {
      if (m.action_items) {
        for (const ai of m.action_items) {
          items.push({
            meetingId: m.id,
            meetingSubject: m.subject || "Untitled",
            meetingDate: m.start_time,
            description: ai.description,
            assignee: ai.assignee,
            dueDate: ai.due_date,
          });
        }
      }
    }
    return items;
  }, [enrichedMeetings]);

  const loading = activeTab === "upcoming" ? loadingUpcoming : loadingEnriched;

  const toggleExpanded = (id: string) => {
    const next = new Set(expandedMeetings);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedMeetings(next);
  };

  const toggleHistory = (id: string) => {
    const next = new Set(openHistories);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenHistories(next);
  };

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }) +
      " at " +
      d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    );
  };

  const formatMeetingTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }) +
      " at " +
      date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
    );
  };

  const tabs: {
    id: MeetingsTabType;
    label: string;
    icon: any;
    count?: number;
  }[] = [
    { id: "recent", label: "Recent Meetings", icon: Clock },
    { id: "upcoming", label: "Upcoming Meetings", icon: Calendar },
    {
      id: "notes",
      label: "Notes / Summaries",
      icon: FileText,
      count: meetingsWithNotes.length,
    },
    {
      id: "action-items",
      label: "Action Items",
      icon: CheckSquare,
      count: allActionItems.length,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div
        className="w-full px-6 py-4 flex-shrink-0"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
              Meetings
            </h1>
          </div>
        </div>

        {/* Tabs + Date Range Filter */}
        <div
          className="mb-4 flex-shrink-0"
          style={{
            marginLeft: "-24px",
            marginRight: "-24px",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          <div className="flex items-center gap-6 -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="pb-3 px-1 text-sm font-medium transition-colors border-b-2 flex items-center gap-2"
                  style={{
                    color:
                      activeTab === tab.id
                        ? "var(--text-primary)"
                        : "var(--text-secondary)",
                    borderColor:
                      activeTab === tab.id
                        ? "var(--text-primary)"
                        : "transparent",
                    fontWeight: activeTab === tab.id ? 700 : 500,
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: "var(--secondary)",
                        color: "var(--text-secondary)",
                      }}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
            {activeTab === "recent" && (
              <div className="ml-auto flex items-center gap-2 pb-3">
                <select
                  value={dateRange}
                  onChange={(e) =>
                    setDateRange(
                      e.target.value as "7days" | "30days" | "90days",
                    )
                  }
                  className="text-xs px-2 py-1 rounded"
                  style={{
                    border: "1px solid var(--border)",
                    color: "var(--text-primary)",
                    backgroundColor: "var(--background)",
                  }}
                >
                  <option value="7days">Last 7 days</option>
                  <option value="30days">Last 30 days</option>
                  <option value="90days">Last 90 days</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        {/* ================================================================
            RECENT TAB -- DB enriched meetings
            ================================================================ */}
        {activeTab === "recent" && (
          <div className="h-full overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div style={{ color: "var(--text-secondary)" }}>
                  Loading meetings...
                </div>
              </div>
            ) : recentMeetings.length === 0 ? (
              <div className="empty-state">
                <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--muted-foreground)" }} />
                <p className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                  No recent meetings found
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentMeetings.map((meeting) => {
                  const isExpanded = expandedMeetings.has(meeting.id);
                  const actionItemCount = meeting.action_items?.length ?? 0;
                  const hasIntel = meeting.enrichment_status === "summarized";

                  return (
                    <div
                      key={meeting.id}
                      className={`rounded-lg p-4 transition-shadow hover:shadow-md ${hasIntel ? "cursor-pointer" : ""}`}
                      style={{
                        backgroundColor: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderLeft: hasIntel
                          ? "3px solid var(--accent)"
                          : "3px solid var(--border)",
                      }}
                      onClick={hasIntel ? (e: React.MouseEvent) => {
                        // Don't toggle if user is selecting text
                        if (window.getSelection()?.toString()) return;
                        // Don't toggle if user clicked an interactive element
                        const target = e.target as HTMLElement;
                        if (target.closest("a, button")) return;
                        toggleExpanded(meeting.id);
                      } : undefined}
                    >
                      {/* Meeting Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3
                              className="text-lg font-semibold"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {meeting.subject || "Untitled meeting"}
                            </h3>
                          </div>
                          <div
                            className="flex items-center gap-4 text-sm"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {meeting.start_time && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  {formatDateTime(meeting.start_time)}
                                </span>
                              </div>
                            )}
                            {meeting.attendees &&
                              meeting.attendees.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  <span>
                                    {meeting.attendees.length} attendees
                                  </span>
                                </div>
                              )}
                            {meeting.organizer_email && (
                              <span
                                className="text-xs"
                                style={{ color: "var(--text-secondary)", opacity: 0.7 }}
                              >
                                {meeting.organizer_email}
                              </span>
                            )}
                          </div>
                        </div>
                        {hasIntel && (
                          <div className="p-2" style={{ color: "var(--text-secondary)" }}>
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Summary */}
                      {meeting.summary && (
                        <div
                          className="mb-3 p-3 rounded"
                          style={{ backgroundColor: "var(--surface)" }}
                        >
                          <h4
                            className="text-xs font-semibold mb-1 uppercase"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            Summary
                          </h4>
                          <p
                            className="text-sm line-clamp-3"
                            style={{ color: "var(--text-primary)" }}
                          >
                            {meeting.summary}
                          </p>
                        </div>
                      )}

                      {/* Key Topics */}
                      {meeting.key_topics && meeting.key_topics.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {meeting.key_topics.map((topic, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor:
                                  "color-mix(in srgb, var(--accent) 10%, transparent)",
                                color: "var(--accent)",
                              }}
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Action Items Preview */}
                      {actionItemCount > 0 && (
                        <div className="mb-3">
                          <h4
                            className="text-xs font-semibold mb-2 uppercase"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            Action Items ({actionItemCount})
                          </h4>
                          <div className="space-y-1">
                            {meeting
                              .action_items!.slice(0, 3)
                              .map((item, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-2 text-sm"
                                  style={{ color: "var(--text-primary)" }}
                                >
                                  <CheckSquare className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {item.description}
                                  </span>
                                  {item.assignee && (
                                    <span
                                      className="text-xs flex-shrink-0"
                                      style={{ color: "var(--text-secondary)" }}
                                    >
                                      {item.assignee}
                                    </span>
                                  )}
                                </div>
                              ))}
                            {actionItemCount > 3 && (
                              <button
                                onClick={() => setActiveTab("action-items")}
                                className="text-xs hover:underline"
                                style={{ color: "var(--accent)" }}
                              >
                                View all {actionItemCount} action items
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div
                          className="mt-4 pt-4 space-y-4"
                          style={{ borderTop: "1px solid var(--border)" }}
                        >
                          {/* Decisions */}
                          {meeting.decisions &&
                            meeting.decisions.length > 0 && (
                              <div>
                                <h4
                                  className="text-xs font-semibold mb-2 uppercase"
                                  style={{ color: "var(--text-secondary)" }}
                                >
                                  Decisions ({meeting.decisions.length})
                                </h4>
                                <div className="space-y-2">
                                  {meeting.decisions.map((d, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-start gap-2 text-sm"
                                      style={{ color: "var(--text-primary)" }}
                                    >
                                      <MessageSquare className="w-3 h-3 mt-1 flex-shrink-0" />
                                      <div>
                                        <span>{d.description}</span>
                                        {d.context && (
                                          <p
                                            className="text-xs mt-0.5"
                                            style={{
                                              color: "var(--text-secondary)",
                                            }}
                                          >
                                            {d.context}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Follow-ups */}
                          {meeting.follow_ups &&
                            meeting.follow_ups.length > 0 && (
                              <div>
                                <h4
                                  className="text-xs font-semibold mb-2 uppercase"
                                  style={{ color: "var(--text-secondary)" }}
                                >
                                  Follow-ups ({meeting.follow_ups.length})
                                </h4>
                                <div className="space-y-1">
                                  {meeting.follow_ups.map((f, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-2 text-sm"
                                      style={{ color: "var(--text-primary)" }}
                                    >
                                      <span
                                        style={{
                                          color: "var(--text-secondary)",
                                        }}
                                      >
                                        -
                                      </span>
                                      <span>{f.description}</span>
                                      {f.owner && (
                                        <span
                                          className="text-xs"
                                          style={{
                                            color: "var(--text-secondary)",
                                          }}
                                        >
                                          ({f.owner})
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Referenced Files */}
                          {meeting.referenced_files &&
                            meeting.referenced_files.length > 0 && (
                              <div>
                                <h4
                                  className="text-xs font-semibold mb-2 uppercase"
                                  style={{ color: "var(--text-secondary)" }}
                                >
                                  Referenced Files (
                                  {meeting.referenced_files.length})
                                </h4>
                                <div className="space-y-1">
                                  {meeting.referenced_files.map((file, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-2 text-sm"
                                      style={{ color: "var(--text-primary)" }}
                                    >
                                      <ExternalLink
                                        className="w-3 h-3 flex-shrink-0"
                                        style={{
                                          color: "var(--text-secondary)",
                                        }}
                                      />
                                      <span className="truncate">{file}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Flagged Claims */}
                          {meeting.flagged_claims &&
                            meeting.flagged_claims.length > 0 && (
                              <div>
                                <h4
                                  className="text-xs font-semibold mb-2 uppercase"
                                  style={{ color: "var(--text-secondary)" }}
                                >
                                  Flagged Claims (
                                  {meeting.flagged_claims.length})
                                </h4>
                                <div className="space-y-2">
                                  {meeting.flagged_claims.map((fc, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-start gap-2 text-sm"
                                    >
                                      <AlertTriangle
                                        className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                                        style={{
                                          color:
                                            fc.accurate === false
                                              ? "var(--error)"
                                              : fc.accurate === true
                                                ? "var(--success)"
                                                : "var(--warning)",
                                        }}
                                      />
                                      <div>
                                        <span
                                          style={{
                                            color: "var(--text-primary)",
                                          }}
                                        >
                                          {fc.claim}
                                        </span>
                                        {fc.verification && (
                                          <p
                                            className="text-xs mt-0.5"
                                            style={{
                                              color: "var(--text-secondary)",
                                            }}
                                          >
                                            {fc.verification}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Transcript Viewer */}
                          {hasIntel && (
                            <div>
                              <button
                                onClick={() =>
                                  setTranscriptMeetingId(
                                    transcriptMeetingId ===
                                      meeting.graph_meeting_id
                                      ? null
                                      : meeting.graph_meeting_id ?? null,
                                  )
                                }
                                className="text-xs font-semibold uppercase flex items-center gap-1.5 transition-colors"
                                style={{ color: "var(--accent)" }}
                              >
                                <FileText className="w-3.5 h-3.5" />
                                {transcriptMeetingId ===
                                meeting.graph_meeting_id
                                  ? "Hide Transcript"
                                  : "View Transcript"}
                              </button>
                              {transcriptMeetingId ===
                                meeting.graph_meeting_id && (
                                <div className="mt-2">
                                  {loadingTranscript ? (
                                    <div
                                      className="text-xs py-4 text-center"
                                      style={{
                                        color: "var(--text-secondary)",
                                      }}
                                    >
                                      Loading transcript...
                                    </div>
                                  ) : meetingDetail?.transcript_text ? (
                                    <pre
                                      className="text-xs whitespace-pre-wrap rounded p-3 max-h-80 overflow-y-auto font-mono leading-relaxed"
                                      style={{
                                        backgroundColor: "var(--surface)",
                                        color: "var(--text-primary)",
                                        border: "1px solid var(--border)",
                                      }}
                                    >
                                      {meetingDetail.transcript_text}
                                    </pre>
                                  ) : (
                                    <p
                                      className="text-xs py-2"
                                      style={{
                                        color: "var(--text-secondary)",
                                      }}
                                    >
                                      Transcript text not available.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ================================================================
            UPCOMING TAB -- attendee-matched context with collapsible history
            ================================================================ */}
        {activeTab === "upcoming" && (
          <div className="h-full overflow-auto p-6">
            {loadingUpcoming && loadingEnriched ? (
              <div className="flex items-center justify-center py-12">
                <div style={{ color: "var(--text-secondary)" }}>
                  Loading meetings...
                </div>
              </div>
            ) : upcomingMeetings.length === 0 &&
              upcomingFromDb.length === 0 ? (
              <div className="text-center py-12">
                <Calendar
                  className="w-12 h-12 mx-auto mb-4"
                  style={{ color: "var(--text-secondary)" }}
                />
                <p style={{ color: "var(--text-secondary)" }}>
                  No upcoming meetings.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Graph-sourced upcoming meetings */}
                {upcomingWithRelated.map(
                  ({ meeting, attendeeEmails, relatedPast }) => {
                    const hasIntelligence = enrichedGraphIds.has(meeting.id);
                    const historyOpen = openHistories.has(meeting.id);

                    return (
                      <div
                        key={meeting.id}
                        className="rounded-lg overflow-hidden transition-shadow hover:shadow-md"
                        style={{
                          backgroundColor: "var(--surface)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        {/* Meeting header */}
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3
                                  className="text-sm font-semibold"
                                  style={{ color: "var(--text-primary)" }}
                                >
                                  {meeting.title}
                                </h3>
                                {hasIntelligence && (
                                  <span
                                    className="text-xs font-medium px-1.5 py-0.5 rounded"
                                    style={{
                                      backgroundColor:
                                        "color-mix(in srgb, var(--accent) 12%, transparent)",
                                      color: "var(--accent)",
                                    }}
                                  >
                                    Intel
                                  </span>
                                )}
                              </div>
                              <div
                                className="flex items-center gap-4 text-xs"
                                style={{ color: "var(--text-secondary)" }}
                              >
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatMeetingTime(meeting.datetime)}
                                </span>
                                {meeting.duration > 0 && (
                                  <span>{meeting.duration} min</span>
                                )}
                                {meeting.attendees?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {meeting.attendees.length}
                                  </span>
                                )}
                              </div>
                              {/* Attendee names */}
                              {meeting.attendees &&
                                meeting.attendees.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {meeting.attendees
                                      .slice(0, 6)
                                      .map((a, i) => (
                                        <span
                                          key={i}
                                          className="text-xs px-2 py-0.5 rounded-full"
                                          style={{
                                            backgroundColor: "var(--secondary)",
                                            color: "var(--text-secondary)",
                                          }}
                                        >
                                          {a.name || a.email}
                                        </span>
                                      ))}
                                    {meeting.attendees.length > 6 && (
                                      <span
                                        className="text-xs px-2 py-0.5 rounded-full"
                                        style={{
                                          backgroundColor: "var(--secondary)",
                                          color: "var(--text-secondary)",
                                          opacity: 0.7,
                                        }}
                                      >
                                        +{meeting.attendees.length - 6}
                                      </span>
                                    )}
                                  </div>
                                )}
                            </div>
                            {meeting.meetingUrl &&
                              /^https?:\/\//i.test(meeting.meetingUrl) && (
                                <a
                                  href={meeting.meetingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs font-medium px-3 py-1.5 rounded-md transition-colors flex-shrink-0"
                                  style={{
                                    backgroundColor:
                                      "color-mix(in srgb, var(--accent) 10%, transparent)",
                                    color: "var(--accent)",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "color-mix(in srgb, var(--accent) 20%, transparent)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "color-mix(in srgb, var(--accent) 10%, transparent)";
                                  }}
                                >
                                  Join
                                </a>
                              )}
                          </div>
                        </div>

                        <MeetingHistorySection
                          meetingId={meeting.id}
                          relatedPast={relatedPast}
                          attendeeEmails={attendeeEmails}
                          historyOpen={historyOpen}
                          onToggle={() => toggleHistory(meeting.id)}
                          formatDateTime={formatDateTime}
                        />
                      </div>
                    );
                  },
                )}

                {/* DB-sourced future meetings (not already shown via Graph) */}
                {upcomingFromDb
                  .filter(
                    (m) =>
                      !m.graph_meeting_id ||
                      !enrichedGraphIds.has(m.graph_meeting_id),
                  )
                  .map((meeting) => {
                    const attendeeEmails = new Set(
                      (meeting.attendees || [])
                        .map((a) => a.email?.toLowerCase())
                        .filter(Boolean) as string[],
                    );
                    const relatedPast =
                      findPastMeetingsByAttendees(
                        attendeeEmails,
                        meeting.id,
                      );
                    const historyOpen = openHistories.has(meeting.id);

                    return (
                      <div
                        key={meeting.id}
                        className="rounded-lg overflow-hidden transition-shadow hover:shadow-md"
                        style={{
                          backgroundColor: "var(--surface)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3
                                  className="text-sm font-semibold"
                                  style={{ color: "var(--text-primary)" }}
                                >
                                  {meeting.subject || "Untitled meeting"}
                                </h3>
                              </div>
                              <div
                                className="flex items-center gap-4 text-xs"
                                style={{ color: "var(--text-secondary)" }}
                              >
                                {meeting.start_time && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatMeetingTime(meeting.start_time)}
                                  </span>
                                )}
                                {meeting.attendees &&
                                  meeting.attendees.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      {meeting.attendees.length}
                                    </span>
                                  )}
                                {meeting.organizer_email && (
                                  <span>{meeting.organizer_email}</span>
                                )}
                              </div>
                              {/* Attendee names */}
                              {meeting.attendees &&
                                meeting.attendees.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {meeting.attendees
                                      .slice(0, 6)
                                      .map((a, i) => (
                                        <span
                                          key={i}
                                          className="text-xs px-2 py-0.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              "var(--secondary)",
                                            color: "var(--text-secondary)",
                                          }}
                                        >
                                          {a.name || a.email}
                                        </span>
                                      ))}
                                    {meeting.attendees.length > 6 && (
                                      <span
                                        className="text-xs px-2 py-0.5 rounded-full"
                                        style={{
                                          backgroundColor:
                                            "var(--secondary)",
                                          color: "var(--text-secondary)",
                                          opacity: 0.7,
                                        }}
                                      >
                                        +{meeting.attendees.length - 6}
                                      </span>
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>

                        <MeetingHistorySection
                          meetingId={meeting.id}
                          relatedPast={relatedPast}
                          attendeeEmails={attendeeEmails}
                          historyOpen={historyOpen}
                          onToggle={() => toggleHistory(meeting.id)}
                          formatDateTime={formatDateTime}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* ================================================================
            NOTES TAB -- Real data from enriched meetings
            ================================================================ */}
        {activeTab === "notes" && (
          <div className="h-full overflow-auto p-6">
            <div className="mb-4">
              <h2
                className="text-lg font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                Meeting Summaries
              </h2>
              <p
                className="text-sm mt-1"
                style={{ color: "var(--text-secondary)" }}
              >
                AI-generated summaries from meeting transcripts
              </p>
            </div>

            {loadingEnriched ? (
              <div className="flex items-center justify-center py-12">
                <div style={{ color: "var(--text-secondary)" }}>
                  Loading...
                </div>
              </div>
            ) : meetingsWithNotes.length === 0 ? (
              <div className="empty-state">
                <FileText
                  className="w-10 h-10 mx-auto mb-3"
                  style={{ color: "var(--muted-foreground)" }}
                />
                <p className="text-sm font-medium mb-1" style={{ color: "var(--text-secondary)" }}>
                  No meeting summaries available yet
                </p>
                <p
                  className="text-xs"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  Summaries are generated automatically when meeting transcripts
                  are processed.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {meetingsWithNotes.map((meeting) => (
                  <div
                    key={meeting.id}
                    className="rounded-lg p-4"
                    style={{
                      backgroundColor: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderLeft: "3px solid var(--accent)",
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3
                          className="text-sm font-semibold"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {meeting.subject || "Meeting Summary"}
                        </h3>
                        <div
                          className="flex items-center gap-4 text-xs mt-1"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          {meeting.start_time && (
                            <span>
                              {formatDateTime(meeting.start_time)}
                            </span>
                          )}
                          {meeting.summarized_at && (
                            <span
                              className="px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor:
                                  "color-mix(in srgb, var(--accent) 10%, transparent)",
                                color: "var(--accent)",
                              }}
                            >
                              AI Summary
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Summary content */}
                    <div
                      className="text-sm whitespace-pre-wrap rounded p-3 max-h-96 overflow-y-auto leading-relaxed"
                      style={{
                        backgroundColor: "var(--surface)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      {meeting.summary}
                    </div>

                    {/* Action items from this meeting */}
                    {meeting.action_items &&
                      meeting.action_items.length > 0 && (
                        <div
                          className="mt-3 pt-3"
                          style={{ borderTop: "1px solid var(--border)" }}
                        >
                          <h4
                            className="text-xs font-semibold mb-2"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            Action Items
                          </h4>
                          <div className="space-y-1">
                            {meeting.action_items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 text-xs"
                                style={{ color: "var(--text-secondary)" }}
                              >
                                <CheckSquare className="w-3 h-3 flex-shrink-0" />
                                <span>{item.description}</span>
                                {item.assignee && (
                                  <span style={{ opacity: 0.7 }}>
                                    ({item.assignee})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ================================================================
            ACTION ITEMS TAB
            ================================================================ */}
        {activeTab === "action-items" && (
          <div className="h-full overflow-auto p-6">
            <div className="mb-4">
              <h2
                className="text-lg font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                Action Items
              </h2>
              <p
                className="text-sm mt-1"
                style={{ color: "var(--text-secondary)" }}
              >
                {allActionItems.length} action item
                {allActionItems.length !== 1 ? "s" : ""} from meeting
                intelligence
              </p>
            </div>

            {loadingEnriched ? (
              <div className="flex items-center justify-center py-12">
                <div style={{ color: "var(--text-secondary)" }}>
                  Loading...
                </div>
              </div>
            ) : allActionItems.length === 0 ? (
              <div className="text-center py-12">
                <CheckSquare
                  className="w-12 h-12 mx-auto mb-4"
                  style={{ color: "var(--text-secondary)" }}
                />
                <p style={{ color: "var(--text-secondary)" }}>
                  No action items found.
                </p>
                <p
                  className="text-sm mt-1"
                  style={{ color: "var(--text-secondary)", opacity: 0.7 }}
                >
                  Action items are extracted automatically from enriched meeting
                  transcripts.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {allActionItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg p-4 transition-shadow hover:shadow-md"
                    style={{
                      backgroundColor: "var(--surface)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <CheckSquare
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                        style={{ color: "var(--text-secondary)" }}
                      />
                      <div className="flex-1">
                        <p
                          className="text-sm font-medium"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {item.description}
                        </p>
                        <div
                          className="flex items-center gap-3 text-xs mt-1"
                          style={{ color: "var(--text-secondary)" }}
                        >
                          <span>From: {item.meetingSubject}</span>
                          {item.meetingDate && (
                            <>
                              <span>-</span>
                              <span>
                                {formatDateTime(item.meetingDate)}
                              </span>
                            </>
                          )}
                          {item.assignee && (
                            <>
                              <span>-</span>
                              <span>Assigned to: {item.assignee}</span>
                            </>
                          )}
                          {item.dueDate && (
                            <>
                              <span>-</span>
                              <span>Due: {item.dueDate}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
